import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft, ScrollText } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useSession } from '@/components/auth/session';
import { Empty, EventLogSection } from '@/components/common/bits';
import { ProgressBar } from '@/components/common/ProgressBar';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Button } from '@/components/ui/button';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { PausedChip } from '@/features/actions/PauseActions';
import { AmendComposer } from '@/features/briefs/AmendComposer';
import { BriefDetail, JobFacts } from '@/features/briefs/BriefDetail';
import { IntegrationCheck } from '@/features/briefs/IntegrationCheck';
import { JobComments } from '@/features/briefs/JobComments';
import { JobDecision, JobVerbs } from '@/features/briefs/JobGate';
import { JobPlan } from '@/features/briefs/JobPlan';
import { MergeOrder } from '@/features/briefs/MergeOrder';
import { PlanQuestions } from '@/features/briefs/PlanQuestions';
import { TaskDetail } from '@/features/tasks/TaskDetail';
import { UsageLine } from '@/features/tasks/TaskParts';
import { api } from '@/lib/api';
import {
  addedDrafts,
  awaitsPlan,
  type BriefProgress,
  briefProgress,
  hasOpenGate,
  isCommentable,
  jobUsageTotals,
  pausableTasks,
  pausedTasks,
  producedNote,
  showsProgress,
} from '@/lib/briefs';
import { unfiledFindingsByTaskKey } from '@/lib/notes';
import { hubKeys } from '@/lib/query';
import { openQuestions } from '@/lib/questions';
import { JobGraphSection } from '@/routes/job-graph';

/**
 * One job, as a page.
 *
 * It was a 400px rail beside the jobs list, and everything a job is had to fit
 * in it: the goal wrapped to eight lines, the plan was a scrolling column inside
 * a scrolling column, the events were a third scroller, and the graph was not
 * there at all - it lived on `/jobs/:id/graph`, reached by a link that threw
 * away the context you were reading it in.
 *
 * A job is the unit a person opens, reads, decides on and comes back to, so it
 * gets a page. What the page does that the rail could not:
 *
 *  - the goal and the plan each at a width where ten steps read without a
 *    nested scroll;
 *  - the graph in place, as the primary view of the plan rather than a route of
 *    its own;
 *  - the gate pinned under the header, because it is why most people opened the
 *    job, with amend, pause and cancel immediately under it;
 *  - the events and the comments at the foot of the page, since both are the
 *    record of what happened rather than the job itself.
 *
 * **Two columns from `xl`, and that is what this page got wrong for a release.**
 * Every region was a band at the page's full 1560px, stacked: the facts, the
 * goal, the plan, the cost, the integration check, the merge order, the graph,
 * the comments, the events. Nine headings and nine cards down a single column,
 * and most of them hold something narrow by nature - a property list, one line
 * of token counts, a two-sentence verdict - so the right two thirds of the page
 * was empty for its whole length while the page itself was a mile long.
 *
 * So the page splits by *what a thing's content actually is*, not by how
 * important it is. The wide column takes everything a person reads or acts
 * inside: the goal, the plan, the step they opened, the graph, the
 * conversation, the journal. The rail takes the things that are a short list of
 * scalars however much room you give them: the job's own properties, what it
 * cost, whether the union was ever checked, the order to merge in. The rail is
 * 340px and does not stick - it ends where its facts end, and the page keeps
 * exactly one scroller.
 *
 * Below `xl` the rail's regions fall back into the single column, after the
 * plan and before the graph, which is the order they are wanted in on a phone.
 *
 * **There is one scroller on this page: the window.** The single exception is
 * the graph's own horizontal one, which a DAG wider than the viewport genuinely
 * needs and which cannot swallow a vertical gesture.
 *
 * The selected step is the search string rather than component state, for the
 * same reason the job itself is a route: a step somebody wants a second opinion
 * on is a link, and it carries the job around it.
 */
export interface JobSearch {
  /** The task id of the step whose detail is open, from `?step=`. */
  step?: string;
}

function AllJobs() {
  return (
    <Link
      to="/jobs"
      className="text-fg-3 hover:text-foreground inline-flex items-center gap-1 text-xs transition-colors duration-100"
    >
      <ArrowLeft className="size-3.5" aria-hidden />
      All jobs
    </Link>
  );
}

/** The header and an empty page under it, for loading and for a bad id. */
function Shell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <ViewHeader title={title} icon={ScrollText} tone="var(--dy-plan)" actions={<AllJobs />} />
      <Page>
        <Surface>{children}</Surface>
      </Page>
    </>
  );
}

/** "3 of 7 done", with the bar and the paused chip, for a section heading. */
function Progress({ progress, paused }: { progress: BriefProgress; paused: number }) {
  // A held step keeps its branch and PR (14 days, docs/JOB-LIFECYCLE.md
  // decision 2); without this clause a job stuck with three of them open reads
  // exactly like one nothing has touched, "0 of 7 done" either way (issue #29).
  const pushedNote = producedNote(progress);
  return (
    <span className="flex items-center gap-2">
      <ProgressBar fraction={progress.done / progress.total} className="w-24 max-sm:hidden" />
      <span className="text-fg-3 tnum text-xs">
        {progress.done} of {progress.total} done
        {pushedNote ? ` · ${pushedNote}` : ''}
      </span>
      {paused ? <PausedChip /> : null}
    </span>
  );
}

export function JobPage() {
  const { briefId } = useParams({ strict: false }) as { briefId: string };
  const { step } = useSearch({ strict: false }) as JobSearch;
  const navigate = useNavigate();
  const { session } = useSession();
  const [target, setTarget] = useState<string | null>(null);
  const [amending, setAmending] = useState(false);
  const detail = useQuery({
    queryKey: hubKeys.brief(briefId),
    queryFn: () => api.brief(briefId),
    // The page carries live state now that the graph is on it, so it takes the
    // same 30s fallback every other live surface has behind the event stream.
    refetchInterval: 30_000,
  });

  /**
   * Selecting a step replaces the history entry rather than stacking one.
   *
   * Clicking through six nodes of a graph is reading, not navigating, and Back
   * should leave the job rather than walk you backwards through what you looked
   * at. The URL still carries the selection, so it is a link either way.
   */
  const selectStep = (taskId: string | null) => {
    void navigate({
      to: '.',
      params: true,
      search: (): JobSearch => (taskId ? { step: taskId } : {}),
      replace: true,
    });
  };
  /** A second click on the step already open closes it. */
  const toggleStep = (taskId: string) => selectStep(taskId === step ? null : taskId);

  if (detail.isPending) {
    return (
      <Shell title="Job">
        <Empty>Loading…</Empty>
      </Shell>
    );
  }
  if (detail.isError) {
    return (
      <Shell title="Job">
        <Empty>{detail.error instanceof Error ? detail.error.message : 'no such job'}</Empty>
      </Shell>
    );
  }

  const { brief, tasks, comments, questions, attachments, events, integrationCheck } = detail.data;
  const needsPlan = awaitsPlan(brief, tasks.length);
  const commentable = isCommentable(brief);
  const progress = briefProgress(tasks);
  const added = addedDrafts(brief, tasks);
  const paused = pausedTasks(tasks);
  const pausable = pausableTasks(tasks);
  const addedKeys = new Set(added.map((task) => task.key));
  const findings = unfiledFindingsByTaskKey(comments, tasks);
  const selected = step ? tasks.find((task) => task.id === step) : undefined;
  const gated = hasOpenGate(brief, tasks);
  // The sum over the steps, with the planning run counted apart from them -
  // a plan that cost 100k tokens to draft is worth seeing next to the six
  // steps it produced, not folded into the same figure (issue #18).
  const usage = jobUsageTotals(tasks);
  const showProgress = showsProgress(brief) && progress.total > 0;
  // The same set that may approve: the person who filed it, or an admin
  // (docs/JOB-LIFECYCLE.md decision 4). The hub enforces it; this only decides
  // whether to draw the verbs.
  const mayShape =
    session.isAdmin ||
    brief.submittedBy.trim().toLowerCase() === session.actor.trim().toLowerCase();

  return (
    <>
      <ViewHeader
        title={brief.title}
        icon={ScrollText}
        tone="var(--dy-plan)"
        actions={
          <>
            {/* The standing verbs - Amend, Pause, Cancel - live in the header
                now (issue #89), one control instead of the full-width card
                that used to sit between the gate and the goal. */}
            <JobVerbs
              brief={brief}
              pausableCount={pausable.length}
              pausedCount={paused.length}
              mayShape={mayShape}
              amending={amending}
              onAmend={() => setAmending((open) => !open)}
            />
            <AllJobs />
          </>
        }
      />
      <Page>
        <div className="dy-stack">
          {/* Who opened this, its state, its repos and every other scalar fact
              about the record now live in one property grid, drawn by
              `BriefDetail` directly under its own title - the ClickUp shape of
              a task page, and the fix for issue #64's "opened by" being said in
              the header's subtitle, an identity line and a meta grid all at
              once. The gate stays first on the page regardless: it is why most
              people opened the job, not a fact about it. */}
          {gated ? (
            // Pinned under the header, which is 4rem tall. The gate wash is
            // opaque, so the plan scrolling behind it does not read through.
            //
            // Only from `lg`, and that is a height decision rather than a width
            // one: the card is two lines and a button row on a desk, and four
            // lines and a 36px button row on a phone, where pinning it would
            // spend a quarter of the screen on something the page opens with
            // anyway.
            <div className="lg:sticky lg:top-16 lg:z-10">
              <JobDecision
                brief={brief}
                tasks={tasks}
                added={added}
                needsPlan={needsPlan}
                openQuestions={openQuestions(questions).length}
              />
            </div>
          ) : null}

          {amending ? (
            <Section>
              <SectionHead title="Amend the job" />
              <Surface>
                <AmendComposer brief={brief} tasks={tasks} onClose={() => setAmending(false)} />
              </Surface>
            </Section>
          ) : null}

          {/* The split. `dy-stack` inside each column keeps the same rhythm
              the page had when it was one, so nothing about a region changes
              except how much width it is asked to fill. */}
          <div className="grid gap-[var(--dy-gap)] xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start">
            <div className="dy-stack min-w-0">
              {/* The goal and the plan. The goal comes first: you read it to judge
              the plan. Both keep the wide column, because both are the thing a
              person is actually reading - a step's title, repo, size, state and
              edge chips still land on one line at every width the page opens
              at, which is what the plan lost when it was a 470px rail. */}
              <Section>
                <SectionHead
                  title="The goal"
                  meta={
                    brief.constraints.length
                      ? `${brief.constraints.length} rule${brief.constraints.length === 1 ? '' : 's'}`
                      : undefined
                  }
                />
                <BriefDetail brief={brief} attachments={attachments} />
              </Section>

              {/* Between the goal and the plan, because that is the order the
                  two are read in: the fork is about the goal and it is what
                  the plan below was written on (docs/PLAN-QUESTIONS.md). It
                  stays after Go, read-only, so the page still says what was
                  decided and by whom. */}
              {questions.length || brief.risks.length || brief.outOfScope.length ? (
                <Section>
                  <SectionHead
                    title="What the planner was unsure about"
                    // "2 open" only while they are: after Go those two were
                    // accepted, and calling a decision somebody made an open
                    // question is the one thing this heading can get wrong.
                    meta={
                      commentable && openQuestions(questions).length
                        ? `${openQuestions(questions).length} open`
                        : undefined
                    }
                  />
                  <PlanQuestions
                    brief={brief}
                    questions={questions}
                    tasks={tasks}
                    answerable={brief.status === 'awaiting_approval'}
                    onSelect={toggleStep}
                  />
                </Section>
              ) : null}

              <Section>
                <SectionHead
                  title={`The plan · ${tasks.length} task${tasks.length === 1 ? '' : 's'}`}
                  actions={
                    showProgress ? (
                      <Progress progress={progress} paused={paused.length} />
                    ) : undefined
                  }
                />
                <Surface>
                  <JobPlan
                    tasks={tasks}
                    onComment={setTarget}
                    commentable={commentable}
                    awaiting={needsPlan}
                    added={addedKeys}
                    mayShape={mayShape}
                    selectedId={step ?? null}
                    onSelect={toggleStep}
                  />
                </Surface>
              </Section>

              <JobGraphSection
                brief={brief}
                tasks={tasks}
                added={addedKeys}
                findings={findings}
                selectedKey={selected?.key ?? null}
                onSelect={toggleStep}
                needsPlan={needsPlan}
              />

              {/* The step the graph or the plan has open. A region of this page
              rather than a sheet over it, so the plan it belongs to is still on
              screen above it and nothing is modal.

              It follows the search string rather than the node, so a link to a
              step this page does not draw - the planning task, or one that
              moved - still opens the step. It just highlights nothing. */}
              {step ? (
                <Section>
                  <SectionHead
                    title={selected ? `Task ${selected.key}` : 'The task'}
                    // The way on to the task's own page is the pane's own, drawn
                    // in its header wherever it appears (`TaskDetail`), so this
                    // head carries only the way out of the selection.
                    actions={
                      <Button variant="ghost" size="xs" onClick={() => selectStep(null)}>
                        Close
                      </Button>
                    }
                  />
                  <Surface>
                    <TaskDetail taskId={step} />
                  </Surface>
                </Section>
              ) : null}

              <Section>
                <SectionHead
                  title="Comments"
                  meta={comments.length ? String(comments.length) : undefined}
                />
                <Surface>
                  <JobComments
                    briefId={brief.id}
                    comments={comments}
                    commentable={commentable}
                    target={target}
                    onTarget={setTarget}
                  />
                </Surface>
              </Section>

              <EventLogSection
                events={events}
                empty="Nothing has happened on this job yet."
                scope="job.events"
              />
            </div>

            {/* The rail: everything about this job that is a short list of
                scalars however much room you give it. Each keeps its own
                heading, because a fact with no name is a number nobody can
                read. */}
            <div className="dy-stack min-w-0">
              <Section>
                <SectionHead title="Details" />
                <JobFacts brief={brief} />
              </Section>

              {usage.work || usage.plan ? (
                <Section>
                  <SectionHead title="Cost" />
                  <Surface>
                    <div className="divide-y">
                      {usage.work ? (
                        <div className="px-gutter py-2">
                          <div className="dy-eyebrow mb-1">The work</div>
                          <UsageLine usage={usage.work} />
                        </div>
                      ) : null}
                      {usage.plan ? (
                        <div className="px-gutter py-2">
                          <div className="dy-eyebrow mb-1">Planning</div>
                          <UsageLine usage={usage.plan} />
                        </div>
                      ) : null}
                    </div>
                  </Surface>
                </Section>
              ) : null}

              {/* Directly above the merge order, because that is the moment it
                  is for: a person about to start clicking Merge, reading the
                  order they may land in, wants to know whether the combination
                  was ever tested at all (issue #26). */}
              <IntegrationCheck
                brief={brief}
                tasks={tasks}
                check={integrationCheck}
                mayShape={mayShape}
              />

              <MergeOrder tasks={tasks} />
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}
